import { useMemo, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { errorView, gateRetryText, sessionApi } from '@krp/client-runtime'
import { Button, StatusMessage } from '@krp/ui-kit'

import { signInRequested } from '../composition/session'
import { useRefetchOnReconnect } from './connection'
import { useAppDispatch, useAppSelector } from './hooks'
import { selectMe, selectStatus } from './sessionSelectors'
import { useGateRetry } from './useGateRetry'
import { useSessionRecovery } from './useSessionRecovery'

/**
 * Вход — по общему механизму (D-085): 401 на ЛЮБОЙ запрос переводит сессию в
 * «истекла», и здесь человек получает кнопку входа с возвратом туда, где был.
 * 403 на «кто я» — ролей приложения нет: на вход НЕ уводим (петля SSO).
 * Но 401 — не приговор: `useSessionRecovery` перепроверяет «кто я», при возврате на вкладку
 * и после 401, и пробует тихий вход; кнопка остаётся, только если не вышло.
 * Открытый экран при истёкшей сессии не размонтируется: человек видит, на чём
 * остановился, а несохранённая работа не исчезает молча.
 *
 * Сервер входа не ответил на первой загрузке (RP-SALE-FAST, К-111): «Повторить», повтор сам с растущей паузой (`useGateRetry`) и
 * перечитывание «кто я», как только вернулась связь, — без F5.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const status = useAppSelector(selectStatus)
  const { refetch } = sessionApi.endpoints.sessionMe.useQuerySubscription(undefined, { skip: status === 'signedOut' })
  const { recovering } = useSessionRecovery(refetch)
  const dispatch = useAppDispatch()
  const { pathname, search } = useLocation()
  const me = useAppSelector(selectMe)
  const failure = useMemo(() => (me.isError ? errorView(me.error) : null), [me.error, me.isError])
  /** Сервер входа не ответил (а не отказал): не 401 и не 403. */
  const outage = failure !== null && failure.kind !== 'unauthorized' && failure.kind !== 'forbidden' ? failure : null
  const { attempts, retryNow } = useGateRetry(outage !== null, refetch)
  useRefetchOnReconnect(refetch, outage !== null)

  const signIn = (
    <Button
      variant="primary"
      onClick={() => dispatch(signInRequested({ returnUrl: `${pathname}${search}` }))}
      actionId="shell.sign-in"
    >
      Войти
    </Button>
  )

  if (status === 'signedOut') {
    return (
      <main className="shell-gate">
        <StatusMessage kind="unauthorized" title="Вы вышли из приложения" action={signIn} />
      </main>
    )
  }

  if (status === 'denied') {
    return (
      <main className="shell-gate">
        <StatusMessage
          kind="forbidden"
          title="Нет доступа к приложению"
          text="У вашей учётной записи нет ролей этого приложения. Обратитесь к администратору."
        />
      </main>
    )
  }

  const known = me.data !== undefined && (status === 'active' || status === 'expired')

  if (!known && status === 'expired' && !recovering) {
    return (
      <main className="shell-gate">
        <StatusMessage
          kind="unauthorized"
          title="Нужно войти"
          text="Вход выполняется через учётную запись Kalmykov."
          action={signIn}
        />
      </main>
    )
  }

  if (!known) {
    if (outage !== null) {
      return (
        <main className="shell-gate">
          <StatusMessage
            kind="unavailable"
            title="Сервер входа недоступен"
            text={outage.message}
            {...(outage.correlationId !== null ? { meta: `Запрос ${outage.correlationId}` } : {})}
            action={
              <>
                <p className="krp-status__text">{gateRetryText(attempts)}</p>
                <Button variant="primary" onClick={retryNow} actionId="shell.gate-retry">
                  Повторить
                </Button>
              </>
            }
          />
        </main>
      )
    }
    return (
      <main className="shell-gate">
        <StatusMessage kind="loading" title="Проверяем вход…" />
      </main>
    )
  }

  return (
    <>
      {status === 'expired' && !recovering && (
        <div className="shell-banner">
          <StatusMessage
            kind="unauthorized"
            title="Сессия завершена"
            text="Войдите снова, чтобы продолжить."
            action={signIn}
          />
        </div>
      )}
      {children}
    </>
  )
}
